// shabba-dashboard/src/components/analytics/OrderBookAnalyzer.tsx
// Live order book microstructure analysis for execution quality assessment

import React, { useMemo } from 'react';
import { BookOpen, Activity, Target, TrendingUp, AlertCircle, BarChart3 } from 'lucide-react';
import { useMarket } from '../../context/MarketContext';
import type { OrderBookEntry } from '../../types';

interface BookMetrics {
  bidAskSpread: number;
  spreadPercent: number;
  depthImbalance: number;    // -1 to 1 (negative = sell pressure)
  liquidityScore: number;    // 0 to 1
  fillProbability: number;   // 0 to 100%
  marketImpact: number;      // Expected price impact in TZS
  bookQuality: 'excellent' | 'good' | 'fair' | 'poor';
  executionRisk: 'low' | 'medium' | 'high';
}

interface OrderBookAnalyzerProps {
  targetSpreadBps?: number;
  maxPosition?: number;
  showHeatmap?: boolean;
}

export default function OrderBookAnalyzer({
  targetSpreadBps = 150,  // 1.5% target spread from DSE Data Lab
  maxPosition = 300,      // Default position size
  showHeatmap = true
}: OrderBookAnalyzerProps) {
  const {
    selectedSymbol,
    currentPrice,
    orderBook,
    labelForSecId,
    recentTrades,
    analytics
  } = useMarket();

  // tick helpers
  const tickFor = (p: number) => (p > 1000 ? 10 : 5);
  const alignToTick = (p: number) => {
    const t = tickFor(p);
    return Math.round(p / t) * t;
  };

  // aggregate orders into tick levels (bids desc, asks asc)
  const { bidLevels, askLevels, maxBidQty, maxAskQty } = useMemo(() => {
    const bidMap = new Map<number, number>();
    const askMap = new Map<number, number>();

    for (const b of orderBook.bids ?? []) {
      const lvl = alignToTick(b.price);
      bidMap.set(lvl, (bidMap.get(lvl) ?? 0) + b.size);
    }
    for (const a of orderBook.asks ?? []) {
      const lvl = alignToTick(a.price);
      askMap.set(lvl, (askMap.get(lvl) ?? 0) + a.size);
    }

    const bidLevels = Array.from(bidMap.entries())
      .map(([price, qty]) => ({ price, qty }))
      .sort((a, b) => b.price - a.price); // best bid first

    const askLevels = Array.from(askMap.entries())
      .map(([price, qty]) => ({ price, qty }))
      .sort((a, b) => a.price - b.price); // best ask first

    const maxBidQty = bidLevels.slice(0, 5).reduce((m, x) => Math.max(m, x.qty), 0) || 1;
    const maxAskQty = askLevels.slice(0, 5).reduce((m, x) => Math.max(m, x.qty), 0) || 1;

    return { bidLevels, askLevels, maxBidQty, maxAskQty };
  }, [orderBook.bids, orderBook.asks]);


  // Calculate comprehensive book metrics using WebSocket analytics where available
  const bookMetrics = useMemo((): BookMetrics | null => {
    if (!orderBook?.bids?.length || !orderBook?.asks?.length || !currentPrice) {
      return null;
    }

    const bestBid = orderBook.bids[0];
    const bestAsk = orderBook.asks[0];
    const midPrice = (bestBid.price + bestAsk.price) / 2;

    // Use spread from analytics if available, otherwise calculate
    const bidAskSpread = analytics?.spreadAnalysis?.currentSpread ?? (bestAsk.price - bestBid.price);
    const spreadPercent = analytics?.spreadAnalysis?.currentSpread 
      ? (analytics.spreadAnalysis.currentSpread / midPrice) * 100
      : (bidAskSpread / midPrice) * 100;

    // Use depth imbalance from analytics if available, otherwise calculate
    let depthImbalance: number;
    if (analytics?.depthImbalance?.imbalance !== undefined) {
      depthImbalance = analytics.depthImbalance.imbalance;
    } else {
      // Fallback calculation
      let totalBidVolume = 0;
      let totalAskVolume = 0;
      const topLevels = Math.min(5, orderBook.bids.length, orderBook.asks.length);

      for (let i = 0; i < topLevels; i++) {
        if (orderBook.bids[i] && orderBook.asks[i]) {
          const bidWeight = 1 / (1 + Math.abs(orderBook.bids[i].price - midPrice) / midPrice);
          const askWeight = 1 / (1 + Math.abs(orderBook.asks[i].price - midPrice) / midPrice);
          totalBidVolume += orderBook.bids[i].size * bidWeight;
          totalAskVolume += orderBook.asks[i].size * askWeight;
        }
      }

      const totalVolume = totalBidVolume + totalAskVolume;
      depthImbalance = totalVolume > 0 ? (totalBidVolume - totalAskVolume) / totalVolume : 0;
    }

    // Liquidity score based on available volume at top levels
    // (Keep calculation as it's not in analytics)
    const topBidVolume = orderBook.bids.slice(0, 3).reduce((sum, bid) => sum + bid.size, 0);
    const topAskVolume = orderBook.asks.slice(0, 3).reduce((sum, ask) => sum + ask.size, 0);
    const liquidityScore = Math.min(1, (topBidVolume + topAskVolume) / 2000); // Normalize to 0-1

    // Use fill probability from analytics if available, otherwise calculate
    const fillProbability = analytics?.fillProbability?.probability !== undefined
      ? analytics.fillProbability.probability * 100 // Convert from 0-1 to 0-100
      : Math.min(100, (Math.min(bestBid.size, bestAsk.size) / maxPosition) * 100);

    // Market impact estimation
    let marketImpact = 0;
    if (maxPosition > 0) {
      let remainingQuantity = maxPosition;
      let totalCost = 0;
      let levels = 0;

      // Simulate walking through the book
      for (const level of orderBook.asks) {
        if (remainingQuantity <= 0) break;
        const takeQuantity = Math.min(remainingQuantity, level.size);
        totalCost += takeQuantity * level.price;
        remainingQuantity -= takeQuantity;
        levels++;
        if (levels >= 5) break; // Max 5 levels
      }

      if (maxPosition > remainingQuantity) {
        const avgPrice = totalCost / (maxPosition - remainingQuantity);
        marketImpact = avgPrice - bestAsk.price;
      }
    }

    // Overall book quality assessment
    let bookQuality: BookMetrics['bookQuality'];
    if (spreadPercent < 0.5 && liquidityScore > 0.8) bookQuality = 'excellent';
    else if (spreadPercent < 1.0 && liquidityScore > 0.6) bookQuality = 'good';
    else if (spreadPercent < 2.0 && liquidityScore > 0.3) bookQuality = 'fair';
    else bookQuality = 'poor';

    // Execution risk assessment
    let executionRisk: BookMetrics['executionRisk'];
    if (fillProbability > 80 && marketImpact < bidAskSpread * 0.2) executionRisk = 'low';
    else if (fillProbability > 50 && marketImpact < bidAskSpread * 0.5) executionRisk = 'medium';
    else executionRisk = 'high';

    return {
      bidAskSpread,
      spreadPercent,
      depthImbalance,
      liquidityScore,
      fillProbability,
      marketImpact,
      bookQuality,
      executionRisk
    };
  }, [orderBook, currentPrice, maxPosition, analytics]);

  if (!bookMetrics) {
    return (
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: 8,
        padding: 16,
        border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <BookOpen size={16} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            Order Book Analyzer
          </h3>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
          No order book data available
        </div>
      </div>
    );
  }

  const targetSpread = currentPrice * (targetSpreadBps / 10000);
  const spreadComparison = bookMetrics.bidAskSpread / targetSpread;

  const getQualityColor = (quality: BookMetrics['bookQuality']) => {
    switch (quality) {
      case 'excellent': return '#10b981';
      case 'good': return '#3b82f6';
      case 'fair': return '#f59e0b';
      case 'poor': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getRiskColor = (risk: BookMetrics['executionRisk']) => {
    switch (risk) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} TZS`;

  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: 8,
      padding: 16,
      border: '1px solid #334155'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOpen size={16} color="#3b82f6" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            Order Book Analyzer
          </h3>
        </div>
        <div style={{
          backgroundColor: `${getQualityColor(bookMetrics.bookQuality)}15`,
          border: `1px solid ${getQualityColor(bookMetrics.bookQuality)}30`,
          borderRadius: 4,
          padding: '4px 8px',
          fontSize: 11,
          fontWeight: 600,
          color: getQualityColor(bookMetrics.bookQuality),
          textTransform: 'capitalize'
        }}>
          {bookMetrics.bookQuality}
        </div>
      </div>

      {/* Current Spread Analysis */}
      <div style={{
        backgroundColor: '#0f172a',
        borderRadius: 6,
        padding: 12,
        marginBottom: 16,
        border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Current Spread</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            {formatCurrency(bookMetrics.bidAskSpread)}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Spread %</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            {bookMetrics.spreadPercent.toFixed(3)}%
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Target Spread ({targetSpreadBps} bps)</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>
            {formatCurrency(targetSpread)}
          </span>
        </div>

        <div style={{
          height: 4,
          backgroundColor: '#334155',
          borderRadius: 2,
          marginTop: 8,
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(100, Math.max(0, (1 - spreadComparison) * 100))}%`,
            backgroundColor: spreadComparison <= 1 ? '#10b981' : '#ef4444',
            borderRadius: 2,
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, textAlign: 'center' }}>
          {spreadComparison > 1 ? 'Wider than target' : 'Within target range'}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        marginBottom: 16
      }}>
        <div style={{
          backgroundColor: '#0f172a',
          borderRadius: 4,
          padding: 10,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>
            DEPTH IMBALANCE
          </div>
          <div style={{
            fontSize: 16,
            fontWeight: 700,
            color: bookMetrics.depthImbalance > 0.1 ? '#10b981' :
                   bookMetrics.depthImbalance < -0.1 ? '#ef4444' : '#f59e0b'
          }}>
            {bookMetrics.depthImbalance > 0 ? '+' : ''}{(bookMetrics.depthImbalance * 100).toFixed(1)}%
          </div>
          <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>
            {bookMetrics.depthImbalance > 0.1 ? 'Buy pressure' :
             bookMetrics.depthImbalance < -0.1 ? 'Sell pressure' : 'Balanced'}
          </div>
        </div>

        <div style={{
          backgroundColor: '#0f172a',
          borderRadius: 4,
          padding: 10,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>
            LIQUIDITY SCORE
          </div>
          <div style={{
            fontSize: 16,
            fontWeight: 700,
            color: bookMetrics.liquidityScore > 0.7 ? '#10b981' :
                   bookMetrics.liquidityScore > 0.4 ? '#f59e0b' : '#ef4444'
          }}>
            {(bookMetrics.liquidityScore * 100).toFixed(0)}%
          </div>
          <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>
            Top 3 levels
          </div>
        </div>
      </div>

      {/* Execution Quality */}
      <div style={{
        backgroundColor: '#0f172a',
        borderRadius: 6,
        padding: 12,
        marginBottom: showHeatmap ? 16 : 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
          <Target size={14} color="#3b82f6" />
          <span style={{ fontSize: 12, color: '#f1f5f9', fontWeight: 600 }}>
            Execution Quality ({maxPosition} shares)
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>
              Fill Probability
            </div>
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: bookMetrics.fillProbability > 80 ? '#10b981' :
                     bookMetrics.fillProbability > 50 ? '#f59e0b' : '#ef4444'
            }}>
              {bookMetrics.fillProbability.toFixed(0)}%
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>
              Market Impact
            </div>
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: bookMetrics.marketImpact < bookMetrics.bidAskSpread * 0.2 ? '#10b981' :
                     bookMetrics.marketImpact < bookMetrics.bidAskSpread * 0.5 ? '#f59e0b' : '#ef4444'
            }}>
              {bookMetrics.marketImpact.toFixed(2)} TZS
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>
              Execution Risk
            </div>
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: getRiskColor(bookMetrics.executionRisk),
              textTransform: 'capitalize'
            }}>
              {bookMetrics.executionRisk}
            </div>
          </div>
        </div>
      </div>

      {/* Liquidity Heatmap */}
      {showHeatmap && orderBook && (
        <div style={{
          backgroundColor: '#0f172a',
          borderRadius: 6,
          padding: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <BarChart3 size={14} color="#3b82f6" />
            <span style={{ fontSize: 12, color: '#f1f5f9', fontWeight: 600 }}>
              Liquidity Heatmap
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* Bids */}
            <div>
              <div style={{ fontSize: 10, color: '#10b981', marginBottom: 6, textAlign: 'center' }}>
                BIDS
              </div>
              {bidLevels.slice(0, 5).map((lvl, index) => {
                const widthPercent = (lvl.qty / maxBidQty) * 100;

                return (
                  <div key={index} style={{ display:'flex', alignItems:'center', marginBottom:2, fontSize:10 }}>
                    <div style={{ width:'45px', color:'#10b981', textAlign:'right' }}>
                      {lvl.price.toFixed(2)}
                    </div>
                    <div style={{ flex:1, margin:'0 6px', position:'relative', height:12 }}>
                      <div style={{
                        position:'absolute',
                        right:0,
                        top:0,
                        height:'100%',
                        width:`${widthPercent}%`,
                        backgroundColor:'#10b98130',
                        borderRadius:2
                      }} />
                    </div>
                    <div style={{ width:'30px', color:'#f1f5f9', fontSize:9 }}>
                      {lvl.qty}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Asks */}
            <div>
              <div style={{ fontSize: 10, color: '#ef4444', marginBottom: 6, textAlign: 'center' }}>
                ASKS
              </div>
              {askLevels.slice(0, 5).map((lvl, index) => {
                const widthPercent = (lvl.qty / maxAskQty) * 100;

                return (
                  <div key={index} style={{ display:'flex', alignItems:'center', marginBottom:2, fontSize:10 }}>
                    <div style={{ width:'30px', color:'#f1f5f9', fontSize:9 }}>
                      {lvl.qty}
                    </div>
                    <div style={{ flex:1, margin:'0 6px', position:'relative', height:12 }}>
                      <div style={{
                        position:'absolute',
                        left:0,
                        top:0,
                        height:'100%',
                        width:`${widthPercent}%`,
                        backgroundColor:'#ef444430',
                        borderRadius:2
                      }} />
                    </div>
                    <div style={{ width:'45px', color:'#ef4444', textAlign:'left' }}>
                      {lvl.price.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}